// pages/reports/InventoryReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    ChevronUp,
    RotateCcw,
    DollarSign,
    TrendingUp,

    PieChart,

    Package,
    AlertCircle,
    RefreshCw,

    Folder,
    Box
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const InventoryReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Format date for API (use LOCAL date parts to avoid timezone shifting the day,
    // e.g. toISOString() would turn 01/06 00:00 IST into the previous day in UTC)
    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };

            const result = await reportActions.getInventoryReport(params);

            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);

    // Filter products by category
    const getFilteredProducts = (products) => {
        if (categoryFilter === 'all') return products;
        return products.filter(p => p.category_id == categoryFilter);
    };

    // Render filter bar
    const renderFilterBar = () => {
        const categoryOptions = [
            { value: 'all', label: 'All Categories' },
            ...(reportData?.category_breakdown?.map(cat => ({
                value: cat.id,
                label: cat.category_name
            })) || [])
        ];

        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Category</label>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions.find(opt => opt.value === categoryFilter)}
                                onChange={(opt) => setCategoryFilter(opt?.value || 'all')}
                                placeholder="Select category"
                            />
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate Report
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;

        const { summary } = reportData;

        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Products</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_products}
                                    </h3>
                                    <small className="text-muted">Active products</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Package size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Stock Value (Cost)</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(summary.total_cost_value)}
                                    </h3>
                                    <small className="text-muted">Cost price</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Stock Value (Selling)</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.total_selling_value)}
                                    </h3>
                                    <small className="text-muted">Selling price</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Potential Profit</p>
                                    <h3 className="mb-0 text-warning">
                                        {reportActions.formatCurrency(summary.potential_profit)}
                                    </h3>
                                    <small className="text-muted">If all sold</small>
                                </div>
                                <div className="bg-warning bg-opacity-10 rounded-circle p-3">
                                    <PieChart size={24} className="text-warning" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'categories', label: 'Categories', icon: <Folder size={16} /> },
            { id: 'movements', label: 'Stock Movements', icon: <RefreshCw size={16} /> },
            { id: 'lowstock', label: 'Low Stock', icon: <AlertCircle size={16} /> },
            { id: 'topselling', label: 'Top Selling', icon: <TrendingUp size={16} /> },
            { id: 'deadstock', label: 'Dead Stock', icon: <Box size={16} /> }
        ];

        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };

    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;

        const { summary } = reportData;

        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Inventory Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Products</td>
                                        <td className="text-end"><strong>{summary.total_products}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Total Quantity in Stock</td>
                                        <td className="text-end"><strong>{summary.total_quantity}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Average Cost Price</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.average_cost)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Average Selling Price</td>
                                        <td className="text-end"><strong>{reportActions.formatCurrency(summary.average_selling_price)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Low Stock Products</td>
                                        <td className="text-end">
                                            <span className="badge bg-warning">{summary.low_stock_count}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Out of Stock Products</td>
                                        <td className="text-end">
                                            <span className="badge bg-danger">{summary.out_of_stock_count}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Category Summary</h6>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th className="text-end">Products</th>
                                            <th className="text-end">Quantity</th>
                                            <th className="text-end">Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.category_breakdown?.slice(0, 5).map((cat, idx) => (
                                            <tr key={idx}>
                                                <td>{cat.category_name}</td>
                                                <td className="text-end">{cat.product_count}</td>
                                                <td className="text-end">{cat.total_quantity}</td>
                                                <td className="text-end">{reportActions.formatCurrency(cat.total_cost_value)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render categories tab
    const renderCategoriesTab = () => {
        if (!reportData?.category_breakdown) return null;

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Category-wise Inventory</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Category</th>
                                    <th className="text-end">Products</th>
                                    <th className="text-end">Total Quantity</th>
                                    <th className="text-end">Cost Value</th>
                                    <th className="text-end">Selling Value</th>
                                    <th className="text-end">Potential Profit</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.category_breakdown.map((cat, idx) => {
                                    const profit = cat.total_selling_value - cat.total_cost_value;
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{cat.category_name}</strong>
                                            </td>
                                            <td className="text-end">{cat.product_count}</td>
                                            <td className="text-end">{cat.total_quantity}</td>
                                            <td className="text-end">{reportActions.formatCurrency(cat.total_cost_value)}</td>
                                            <td className="text-end">{reportActions.formatCurrency(cat.total_selling_value)}</td>
                                            <td className={`text-end ${profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {reportActions.formatCurrency(profit)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render stock movements tab
    const renderMovementsTab = () => {
        if (!reportData?.stock_movements) return null;

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Stock Movements</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th className="text-end">Stock In</th>
                                    <th className="text-end">Stock Out</th>
                                    <th className="text-end">Net Change</th>
                                    <th className="text-end">Movements</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.stock_movements.map((mov, idx) => {
                                    const netChange = mov.stock_in - mov.stock_out;
                                    return (
                                        <tr key={idx}>
                                            <td>{reportActions.formatDate(mov.date)}</td>
                                            <td className="text-end text-success">{mov.stock_in}</td>
                                            <td className="text-end text-danger">{mov.stock_out}</td>
                                            <td className={`text-end ${netChange >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {netChange}
                                            </td>
                                            <td className="text-end">{mov.total_movements}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render low stock tab
    const renderLowStockTab = () => {
        if (!reportData?.low_stock_products) return null;

        return (
            <div className="card">
                <div className="card-header bg-warning bg-opacity-10">
                    <h6 className="mb-0 text-warning">Low Stock Products</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Category</th>
                                    <th className="text-end">Current Stock</th>
                                    <th className="text-end">Alert Quantity</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.low_stock_products.map((product, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{product.product_name}</strong>
                                        </td>
                                        <td>{product.sku}</td>
                                        <td>{product.category_name || '-'}</td>
                                        <td className="text-end">
                                            <span className={product.quantity === 0 ? 'text-danger fw-bold' : 'text-warning fw-bold'}>
                                                {product.quantity}
                                            </span>
                                        </td>
                                        <td className="text-end">{product.alert_quantity}</td>
                                        <td>
                                            {product.quantity === 0 ? (
                                                <span className="badge bg-danger">Out of Stock</span>
                                            ) : (
                                                <span className="badge bg-warning">Low Stock</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render top selling tab
    const renderTopSellingTab = () => {
        if (!reportData?.top_selling_products) return null;

        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Top Selling Products</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Category</th>
                                    <th className="text-end">Quantity Sold</th>
                                    <th className="text-end">Sales Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getFilteredProducts(reportData.top_selling_products).map((product, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{product.product_name}</strong>
                                        </td>
                                        <td>{product.sku}</td>
                                        <td>{product.category_name || '-'}</td>
                                        <td className="text-end">{product.sold_quantity}</td>
                                        <td className="text-end">{reportActions.formatCurrency(product.sold_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Render dead stock tab
    const renderDeadStockTab = () => {
        if (!reportData?.dead_stock_products) return null;

        return (
            <div className="card">
                <div className="card-header bg-secondary bg-opacity-10">
                    <h6 className="mb-0 text-secondary">Dead Stock (No Movement)</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Category</th>
                                    <th className="text-end">Stock Quantity</th>
                                    <th className="text-end">Stock Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getFilteredProducts(reportData.dead_stock_products).map((product, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{product.product_name}</strong>
                                        </td>
                                        <td>{product.sku}</td>
                                        <td>{product.category_name || '-'}</td>
                                        <td className="text-end">{product.quantity}</td>
                                        <td className="text-end">{reportActions.formatCurrency(product.stock_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Inventory Report...</h5>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Inventory Report</h4>
                            <h6>Track your stock levels and movements</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filter Bar */}
                {renderFilterBar()}

                {/* Summary Cards */}
                {renderSummaryCards()}

                {/* Tabs */}
                {renderTabs()}

                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'categories' && renderCategoriesTab()}
                {activeTab === 'movements' && renderMovementsTab()}
                {activeTab === 'lowstock' && renderLowStockTab()}
                {activeTab === 'topselling' && renderTopSellingTab()}
                {activeTab === 'deadstock' && renderDeadStockTab()}
            </div>
        </div>
    );
};

export default InventoryReport;